import type { EditorialContent } from '@cobalt/utilities';
import { mapEditorialContent } from '@cobalt/utilities';
import EventBusInstance from '@core/eventbus';
import LivePollingInstance from '@live/pollingHandler';
import { language } from '@utilities/browser';
import { splitArray, mergeArrays, isEqual } from '@utilities/collection-utils';
import { formatUrl } from '@utilities/fetch';
import { MemoryCache } from '@utilities/memory-cache';
import { debounce } from '@utilities/throttle';
import { cobaltApiUrl } from '@utilities/WindowContext';

const arrayMaxSize = 10;
const videoKinds = ['Highlights', 'Medium Highlights', 'Long Highlights', 'Finals'];

export class MatchHighlightsService {
  matchIds: string[] = [];
  savedUrls: string[] = [];
  cache = new MemoryCache<string, EditorialContent>();

  getFilters = (ids: string[]) => ({
    type: 'videostory',
    'param.attributes.main.kind': videoKinds,
    'param.attributes.footballEntities.match.matchId': ids,
    'param.attributes.language': language,
    sorting: '-attributes.firstPublicationDate',
    aggregator: 'lightnodejson',
    limit: ids.length * videoKinds.length,
  });

  getSearchUrls = () =>
    splitArray(this.matchIds, arrayMaxSize).map((ids) =>
      formatUrl(cobaltApiUrl, 'cachedsearch/build', this.getFilters(ids)),
    );

  on = (matchIds: string[], callback: (video: EditorialContent) => unknown, context?: any) => {
    this.matchIds = mergeArrays(this.matchIds, matchIds)
      .filter(Boolean)
      .toSorted((a, b) => a.localeCompare(b));
    matchIds.forEach((matchId) => {
      EventBusInstance.on(`matchHighlightVideo_${matchId}`, callback, context);

      const cached = this.cache.get(matchId);
      if (cached) {
        callback.call(context, cached);
      }
    });

    this._attach();
  };

  _dispatch = (response) => {
    const videos: EditorialContent[] = response?.result?.map((res) => mapEditorialContent(res?.nodeData)) ?? [];

    videos
      .filter((v) => v?.relations?.some((r) => !!r.match?.matchId))
      .forEach((video) => {
        const matchId = video?.relations?.find((r) => !!r.match?.matchId)?.match?.matchId;
        this.cache.set(matchId, video);
        EventBusInstance.dispatch(`matchHighlightVideo_${matchId}`, video);
      });
  };

  _attach = debounce(
    () => {
      const newUrls = this.getSearchUrls();
      if (isEqual(this.savedUrls, newUrls)) {
        return;
      }

      // if the url is changed change the polling cnnection
      this.savedUrls.forEach((url) => LivePollingInstance.off(url, this._dispatch, this));

      this.savedUrls = newUrls;

      this.savedUrls.forEach((url) => {
        LivePollingInstance.on(url, [{ key: 'x-api-key', value: window.cobaltApiKey }], 60000, this._dispatch, this);
      });
    },
    250,
    false,
  );

  off = (matchIds: string[], callback: (video: EditorialContent) => unknown, context?: any) => {
    this.matchIds = this.matchIds.filter((id) => !matchIds.includes(id)).toSorted((a, b) => a.localeCompare(b));
    matchIds.forEach((matchId) => {
      EventBusInstance.off(`matchHighlightVideo_${matchId}`, callback, context);
    });

    this._attach();
  };

  static readonly instance = new MatchHighlightsService();
}

const MatchHighlightsServiceInstance = MatchHighlightsService.instance;
export default MatchHighlightsServiceInstance;
